import { Box, Tooltip, Typography } from '@mui/material';
import { Info } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Modality, modalityDisplayName } from '~/domain/dicom/modality';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type Result = {
    compatibleModels: PredictionModel[];
    emptyMessage: string | undefined;
    labelAdornment: React.ReactNode | undefined;
    /** False when a model is selected but not in `compatibleModels`. */
    isSelectedModelCompatible: boolean;
};

export default function useModalityCompatibleModels(
    availableModels: PredictionModel[],
    modality: Modality | undefined,
    selectedModel: PredictionModel | undefined,
    setSelectedModel: (model: PredictionModel | undefined) => void
): Result {
    const intl = useIntl();

    // An undetermined source modality — undefined, or UNKNOWN produced by
    // strToModality on a missing/non-standard DICOM value — must fail OPEN:
    // show every model rather than hiding all modality-specific ones behind a
    // misleading "no compatible models for UNKNOWN" message. The backend still
    // enforces input/target modality validity on submit.
    const effectiveModality =
        modality && modality !== Modality.UNKNOWN ? modality : undefined;
    const modalityName = effectiveModality
        ? modalityDisplayName(effectiveModality)
        : undefined;

    const compatibleModels = useMemo(() => {
        if (!effectiveModality) {
            return availableModels;
        }
        return availableModels.filter(
            (model) =>
                model.inputModalities.length === 0 ||
                model.inputModalities.includes(effectiveModality)
        );
    }, [availableModels, effectiveModality]);

    // Submit-boundary guard: a selection is valid only if it's in the
    // compatible set. Computed synchronously (the clear effect below runs after
    // render), so callers can block submit in the same render a selection turns
    // incompatible. `true` when nothing is selected — Start is gated on
    // presence separately.
    const isSelectedModelCompatible =
        !selectedModel ||
        compatibleModels.some((m) => m.name === selectedModel.name);

    // Keep the selection within the compatible set: drop a selected model once
    // it's no longer compatible with the current source modality (e.g. modality
    // resolved after selection). Guarded on availableModels so a transient
    // empty list (mid-load) doesn't clear a valid selection.
    useEffect(() => {
        if (
            selectedModel &&
            availableModels.length > 0 &&
            !isSelectedModelCompatible
        ) {
            setSelectedModel(undefined);
        }
    }, [
        availableModels,
        isSelectedModelCompatible,
        selectedModel,
        setSelectedModel,
    ]);

    const hiddenIncompatibleCount =
        availableModels.length - compatibleModels.length;
    const hasIncompatibleModels = hiddenIncompatibleCount > 0;
    const showHiddenNotice =
        hasIncompatibleModels &&
        compatibleModels.length > 0 &&
        !!effectiveModality;

    // Only when the filter leaves *nothing* selectable (and the emptiness is
    // caused by modality filtering, not an unloaded list) — partial hiding is
    // surfaced via labelAdornment instead.
    const emptyMessage =
        compatibleModels.length === 0 &&
        hasIncompatibleModels &&
        effectiveModality
            ? intl.formatMessage(
                  { id: 'noCompatibleModelsForModality' },
                  { modality: modalityDisplayName(effectiveModality) }
              )
            : undefined;

    let labelAdornment: React.ReactNode | undefined;
    if (showHiddenNotice) {
        const title = intl.formatMessage(
            { id: 'someModelsHiddenForModality' },
            { count: hiddenIncompatibleCount }
        );
        const reason = intl.formatMessage(
            { id: 'someModelsHiddenForModality.reason' },
            { count: hiddenIncompatibleCount, modality: modalityName }
        );
        const tooltip = (
            <Box>
                <Typography fontSize={12}>{title}</Typography>
                <Typography fontSize={11} color="text.secondary">
                    {reason}
                </Typography>
            </Box>
        );
        labelAdornment = (
            <Tooltip title={tooltip}>
                <Box
                    component="span"
                    tabIndex={0}
                    role="img"
                    aria-label={`${title}. ${reason}`}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    <Info size={12} aria-hidden />
                </Box>
            </Tooltip>
        );
    }

    return {
        compatibleModels,
        emptyMessage,
        labelAdornment,
        isSelectedModelCompatible,
    };
}
